/**
 * @fileoverview `convexQueryOrIdle` — build Convex query options only once the
 * arguments are real.
 *
 * TanStack's `enabled: false` does not stop `@convex-dev/react-query` from
 * opening the Convex subscription: the adapter subscribes on the query cache's
 * "added" event, which fires when the observer is created, disabled or not
 * (see `ConvexQueryClient.subscribeInner`). A query built with a placeholder
 * id to satisfy TypeScript — `jobId: (jobId as Id<"jobs">) ?? ("" as Id<"jobs">)`
 * — therefore still runs on the server and logs
 * `ArgumentValidationError: Path .jobId Value ""`, once per resubscribe.
 *
 * Pass `undefined` for the args instead and this returns inert options: a
 * non-Convex query key the adapter ignores, disabled, resolving to undefined.
 * `data` is typed as if the query ran, so call sites read the same either way.
 */
import { convexQuery } from "@convex-dev/react-query";
import { getFunctionName, type FunctionReference } from "convex/server";
import type { FunctionArgs } from "convex/server";

export function convexQueryOrIdle<
  Query extends FunctionReference<"query", "public">,
>(query: Query, args: FunctionArgs<Query> | undefined) {
  const options = convexQuery(query, (args ?? {}) as FunctionArgs<Query>);
  if (args) return options;
  return {
    ...options,
    // Not a `["convexQuery", ...]` key, so the adapter never subscribes.
    queryKey: ["idleConvexQuery", getFunctionName(query)],
    queryFn: () => undefined,
    enabled: false,
  } as unknown as typeof options;
}
